import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const placeholderImg =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360"><rect width="100%" height="100%" fill="#f1f5f9"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#94a3b8" font-family="sans-serif" font-size="20">No image</text></svg>`
  );

function currencyFmt(amt, ccy = 'USD') {
  const n = Number(amt ?? 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: ccy,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

export default function StoreTab({ groupId }) {
  const { t } = useTranslation();

  const [group, setGroup] = useState(null);
  const [products, setProducts] = useState([]);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: 0,
    stock: '',
    image: '',
    active: true,
  });


const [editingId, setEditingId] = useState(null);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      name: '',
      description: '',
      price: 0,
      stock: '',
      image: '',
      active: true,
    });
    setShowProductDialog(true);
  };

  const openEdit = (product) => {
    if (!product) return;
    setEditingId(product.id);
    setForm({
      name: product.name || '',
      description: product.description || '',
      price: product.price ?? 0,
      stock: product.stock ?? '',
      image: product.image || '',
      active: product.active !== false,
    });
    setShowProductDialog(true);
  };

  const fetchStore = useCallback(
    async (signal) => {
      try {
        const [g, p] = await Promise.all([
          api.get(`/groups/${groupId}`),
          api.get(`/groups/${groupId}/products?active_only=false`),
        ]);
        if (!signal?.aborted) {
          setGroup(g.data || null);
          setProducts(p.data || []);
        }
      } catch {
        if (!signal?.aborted) {
          toast.error(t('groupAdminTabs.store.toastLoadError'));
        }
      }
    },
    [groupId, t]
  );

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
            const payload = {
        ...form,
        stock: form.stock !== '' && form.stock != null ? parseInt(form.stock, 10) : null,
      };

      if (editingId) {
        await api.put(`/groups/${groupId}/products/${editingId}`, payload);
        toast.success(
          t('groupAdminTabs.store.toastUpdateSuccess', {
            defaultValue: 'Product updated',
          })
        );
      } else {
        await api.post(`/groups/${groupId}/products`, payload);
        toast.success(t('groupAdminTabs.store.toastCreateSuccess'));
      }
            setShowProductDialog(false);
      setEditingId(null);
      setForm({
        name: '',
        description: '',
        price: 0,
        stock: '',
        image: '',
        active: true,
      });
      const controller = new AbortController();
      fetchStore(controller.signal);
    } catch {
      toast.error(
        editingId
          ? t('groupAdminTabs.store.toastUpdateError', {
              defaultValue: 'Failed to update product',
            })
          : t('groupAdminTabs.store.toastCreateError')
      );
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const id = setTimeout(() => {
      fetchStore(controller.signal);
    }, 0);
    return () => {
      controller.abort();
      clearTimeout(id);
    };
  }, [fetchStore]);

  const currency = group?.currency || 'USD';

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">
          {t('groupAdminTabs.store.title')}
        </h2>
        <Button onClick={openCreate}>
          <Plus size={16} className="mr-2" />
          {t('groupAdminTabs.store.addProduct')}
        </Button>
      </div>

      {products.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            {t('groupAdminTabs.store.none')}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p) => (
            <Card key={p.id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>{p.name}</span>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => openEdit(p)}
                      title={t('groupAdminTabs.store.edit', { defaultValue: 'Edit' })}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={async () => {
                        const ok = window.confirm(
                          t('groupAdminTabs.store.deleteConfirm', {
                            defaultValue: 'Delete this product?',
                          })
                        );
                        if (!ok) return;
                        try {
                          await api.delete(`/groups/${groupId}/products/${p.id}`);
                          toast.success(
                            t('groupAdminTabs.store.toastDeleteSuccess', {
                              defaultValue: 'Product deleted',
                            })
                          );
                          const controller = new AbortController();
                          fetchStore(controller.signal);
                        } catch (e) {
                          toast.error(
                            e?.response?.data?.detail ||
                              t('groupAdminTabs.store.toastDeleteError', {
                                defaultValue: 'Failed to delete product',
                              })
                          );
                        }
                      }}
                      title={t('groupAdminTabs.store.delete', { defaultValue: 'Delete' })}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  <Badge variant={p.active ? 'default' : 'destructive'}>
                    {p.active
                      ? t('groupAdminTabs.store.active')
                      : t('groupAdminTabs.store.inactive')}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {currencyFmt(p.price, p.currency || currency)}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-slate-600">
                <img
                  src={p.image || placeholderImg}
                  alt={p.name}
                  className="w-full h-36 object-cover rounded mb-3"
                  onError={(e) => {
                    e.target.src = placeholderImg;
                  }}
                />
                <p className="mb-2">{p.description}</p>
                {p.stock != null && (
                  <p>
                    <strong>{t('groupAdminTabs.store.stock')}:</strong>{' '}
                    {p.stock}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? t('groupAdminTabs.store.editDialogTitle', { defaultValue: 'Edit product' }) : t('groupAdminTabs.store.dialogTitle')}
            </DialogTitle>
            <DialogDescription>
              {editingId ? t('groupAdminTabs.store.editDialogDescription', { defaultValue: 'Update this product in your store.' }) : t('groupAdminTabs.store.dialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveProduct} className="space-y-3">
            <div>
              <Label>{t('groupAdminTabs.store.name')}</Label>
              <Input
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                required
              />
            </div>
            <div>
              <Label>{t('groupAdminTabs.store.description')}</Label>
              <Textarea
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('groupAdminTabs.store.price')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, price: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label>{t('groupAdminTabs.store.stock')}</Label>
                <Input
                  type="number"
                  value={form.stock}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, stock: e.target.value }))
                  }
                />
              </div>
            </div>
            <div>
              <Label>{t('groupAdminTabs.store.imageUrl')}</Label>
              <Input
                value={form.image}
                onChange={(e) =>
                  setForm((f) => ({ ...f, image: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{t('groupAdminTabs.store.status')}</Label>
              <Select
                value={String(form.active)}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, active: v === 'true' }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('groupAdminTabs.store.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">
                    {t('groupAdminTabs.store.active')}
                  </SelectItem>
                  <SelectItem value="false">
                    {t('groupAdminTabs.store.inactive')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowProductDialog(false)}
              >
                {t('groupAdminTabs.store.cancel')}
              </Button>
              <Button type="submit">
                {t('groupAdminTabs.store.create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
